import React from 'react';
import { UserModel } from './useUserManagement';
import { SUSPEND_DURATIONS } from './userTypes';
import { AdminModal } from '../../pages/admin/ConfirmationDialogs';

const UserModals: React.FC<{ model: UserModel }> = ({ model }) => {
  const {
    suspendModal, setSuspendModal, suspendDuration, setSuspendDuration,
    suspendReason, setSuspendReason, suspending, handleSuspend,
    deleteModal, setDeleteModal, deleteReason, setDeleteReason,
    deleteConfirmEmail, setDeleteConfirmEmail, deleting, handleDelete, inputStyle,
  } = model;

  return (
    <>
      {/* Suspend Modal */}
      {suspendModal && (
        <AdminModal ariaLabel="Suspend account" onCancel={() => setSuspendModal(null)}>
            <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--rp-text)' }}>Suspend Account</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>{suspendModal.email}</p>

            <div className="mb-4">
              <span className="text-xs font-semibold mb-2 block" style={{ color: 'var(--rp-text-secondary)' }} id="suspend-duration-label">Duration</span>
              <div className="flex flex-wrap gap-2" role="group" aria-labelledby="suspend-duration-label">
                {SUSPEND_DURATIONS.map(d => (
                  <button key={d.value}
                    onClick={() => setSuspendDuration(d.value)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      backgroundColor: suspendDuration === d.value ? (d.value === 'permanent' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)') : 'var(--rp-surface-secondary)',
                      color: suspendDuration === d.value ? (d.value === 'permanent' ? 'var(--admin-error)' : 'var(--admin-warning)') : 'var(--rp-text-secondary)',
                      border: `1px solid ${suspendDuration === d.value ? (d.value === 'permanent' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)') : 'var(--rp-border)'}`,
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="suspend-reason" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Reason *</label>
              <textarea id="suspend-reason" value={suspendReason}
                onChange={e => setSuspendReason(e.target.value)}
                placeholder="Why is this account being suspended?"
                className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={3} style={inputStyle}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setSuspendModal(null)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
              <button onClick={handleSuspend} disabled={suspending || !suspendReason.trim()}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-all"
                style={{ backgroundColor: 'var(--admin-warning-strong)' }}
              >
                {suspending ? 'Suspending...' : `Suspend for ${SUSPEND_DURATIONS.find(d => d.value === suspendDuration)?.label}`}
              </button>
            </div>
        </AdminModal>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <AdminModal ariaLabel="GDPR account deletion" onCancel={() => setDeleteModal(null)}>
            <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--admin-error)' }}>GDPR Account Deletion</h3>
            <p className="text-sm mb-2" style={{ color: 'var(--rp-text-secondary)' }}>{deleteModal.email}</p>

            <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--admin-error)' }}>This action is IRREVERSIBLE</p>
              <ul className="text-xs space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
                <li>• Profile, settings, vehicles, contacts — deleted</li>
                <li>• Chat messages — anonymized to "[Message deleted]"</li>
                <li>• Ratings — anonymized (kept for aggregate stats)</li>
                <li>• Events hosted — deleted (cascades to participants)</li>
                <li>• Auth account — permanently removed</li>
              </ul>
            </div>

            <div className="mb-4">
              <label htmlFor="delete-reason" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Reason *</label>
              <textarea id="delete-reason" value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                placeholder="Why is this account being deleted?"
                className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} style={inputStyle}
              />
            </div>

            <div className="mb-6">
              <label htmlFor="delete-confirm-email" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>
                Type the user's email to confirm: <span style={{ color: 'var(--admin-error)' }}>{deleteModal.email}</span>
              </label>
              <input id="delete-confirm-email" type="text" value={deleteConfirmEmail}
                onChange={e => setDeleteConfirmEmail(e.target.value)}
                placeholder={deleteModal.email}
                className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteModal(null)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
              <button onClick={handleDelete}
                disabled={deleting || !deleteReason.trim() || deleteConfirmEmail !== deleteModal.email}
                className="admin-btn admin-btn-danger disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Account Permanently'}
              </button>
            </div>
        </AdminModal>
      )}
    </>
  );
};

export default UserModals;
